/**
 * components/TxnNilaiCell.js
 * Shared "Total Nilai" table cell — identical between Penjualan's and Pembelian's tables
 * (Phase T1g). Stacks: the total and an amber "Sisa: Rp X" line when the transaction isn't
 * Lunas (omitted entirely once it is). **Nilai column split (Brief 3 of the sidebar/dashboard
 * redesign set)**: the per-item price breakdown that used to render here (a `.nilai-cell__items`
 * block, muted and smaller) moved OUT to `TxnCostCells.js`'s own `SubtotalBarangCell` — the
 * single "Nilai" column was split into four (Subtotal Barang / Rincian Tambahan / Total Nilai /
 * Total Setelah Biaya), and the item breakdown belongs with the goods-only subtotal, not
 * duplicated here too. This component (and this file) otherwise keeps its exact prior contract
 * — same props, same `<td className="nilai-cell">`, same Total/Sisa/progress-bar stack — only
 * the item-breakdown block itself was removed.
 *
 * **Vertically CENTERED, not top-aligned (dashboard alignment fix, round 3)** — this `<td>`
 * used to be unconditionally top-aligned (no rule of its own at all, falling through to the
 * base `.txn-table td { vertical-align: top }` default); `styles.css`'s shared-center-line
 * group now includes `.nilai-cell` directly, replacing that default. A CSS-only change — this
 * component renders nothing differently; `vertical-align` only ever repositions the cell's
 * entire content as one rigid block, so the Total/Sisa/progress-bar stack's own internal order
 * is untouched. `SubtotalBarangCell` (`TxnCostCells.js`, a sibling column) deliberately stays
 * top-aligned, so the two can visibly diverge on a row where neither is the tallest content.
 *
 * % terbayar progress bar (reintroduced post-T3): partial payments are now real (Bayar
 * Tagihan, the payment-correction rework), so a bar here is no longer decoration-only — it
 * renders via the shared `PaymentProgress` export below, gated the same way the Sisa line
 * already is (Belum Lunas only), sitting right after Sisa.
 * **`pages/PiutangUtang.js` renders this component DIRECTLY (the Piutang & Utang row
 * redesign)**, exactly like Penjualan.js/Pembelian.js — not a special case anymore. Before
 * that redesign, PiutangUtang rendered its own separate ad hoc Nilai Total/Outstanding columns
 * and only reused this file's standalone `PaymentProgress` export, wrapped in its own
 * `.piutang-num-wrap` to keep the bar absolutely positioned (out of normal flow, so it
 * wouldn't grow the cell) — that whole mechanism is retired; `PaymentProgress`'s bar now
 * always renders in normal flow, in every context it's used. The percentage itself comes from
 * `utils/txnStatus.js`'s shared `pctPaid(txn)`, the
 * same formula `TransactionDetailModal`'s own Sisa Tagihan bar uses, so all three bars can
 * never disagree on the number they show.
 *
 * T1g-fix: the two parts are wrapped in `.nilai-cell__stack`, a flex column with
 * `align-items: flex-end` — every line's own right edge is forced to coincide by
 * construction, not by each line merely happening to fill the cell's width, and one `gap`
 * gives them consistent spacing (the old ad hoc per-line margins didn't match each other).
 *
 * **Type-based total coloring (icon/color differentiation fix)**: green for PENJUALAN
 * (revenue), red for PEMBELIAN (expense), unchanged `var(--blue-deep)` for TRANSFER — a
 * Transfer moves stock between Pusat/Cabang, not income or expense, so it deliberately
 * doesn't get a green/red treatment. Inline `style`, matching this app's established
 * pattern for row-data-driven color (e.g. the Stok column's own red/green in Penjualan.js/
 * Pembelian.js) rather than a new CSS class per type. Only the total gets this — the amber
 * Sisa line carries its own, unrelated meaning (an outstanding-balance warning) that shouldn't
 * be overridden. This is deliberately the ONLY one of the four money columns that carries
 * status color — Subtotal Barang and Total Setelah Biaya are both neutral black/bold by
 * design (see `TxnCostCells.js`'s own header comment).
 *
 * `TOTAL_COLOR_BY_TYPE` stays exported (used directly by this component, above) — no other
 * consumer needs the bare map anymore now that `pages/PiutangUtang.js` renders this component
 * wholesale instead of reimplementing its own colored total `<td>`.
 */
import React from "react";
import { fmtQty } from "../utils/idGenerators";
import { txnTotal, txnSisa, txnStatus, pctPaid } from "../utils/txnStatus";

export const TOTAL_COLOR_BY_TYPE = {
  PENJUALAN: "var(--green)",
  PEMBELIAN: "var(--red)",
};

/** Shared %-paid bar + "N% terbayar" label — see this file's own header comment. */
export function PaymentProgress({ pct }) {
  return (
    <div className="nilai-cell__progress">
      <div className="nilai-cell__bar"><div className="nilai-cell__bar-fill" style={{ width: `${pct}%` }} /></div>
      <div className="nilai-cell__pct">{pct}% terbayar</div>
    </div>
  );
}

export default function TxnNilaiCell({ txn }) {
  const total = txnTotal(txn);
  const sisa = txnSisa(txn);
  const status = txnStatus(txn);
  const totalColor = TOTAL_COLOR_BY_TYPE[txn.type] || "var(--blue-deep)";

  return (
    <td className="nilai-cell">
      <div className="nilai-cell__stack">
        <div className="nilai-cell__total" style={{ color: totalColor }}>{fmtQty(total)}</div>
        {status !== "LUNAS" && <div className="nilai-cell__sisa">Sisa: Rp {fmtQty(sisa)}</div>}
        {status !== "LUNAS" && <PaymentProgress pct={pctPaid(txn)} />}
      </div>
    </td>
  );
}
